import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Check, X, Clock, Building, Package, User, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FormattedDate from '../components/FormattedDate';

const PackRequests = () => {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRequests = async () => {
        try {
            const res = await api.get('/admin/pack-requests');
            setRequests(res.data);
        } catch (error) {
            console.error("Error fetching requests", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleApprove = async (id) => {
        if (!window.confirm('Confirmer l\'approbation ?')) return;
        try {
            await api.put(`/admin/pack-requests/${id}/approve`);
            fetchRequests();
        } catch (error) {
            console.error("Error approving request", error);
            alert("Erreur lors de l'approbation");
        }
    };

    const handleReject = async (id) => {
        if (!window.confirm('Rejeter cette demande ?')) return;
        try {
            await api.put(`/admin/pack-requests/${id}/reject`);
            fetchRequests();
        } catch (error) {
            console.error("Error rejecting request", error);
            alert("Erreur lors du rejet");
        }
    };

    if (isLoading) {
        return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div></div>;
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="p-2 -ml-2 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg transition-colors"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Demandes d'Attribution de Pack</h1>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 uppercase font-medium">
                            <tr>
                                <th className="px-6 py-4">Admin</th>
                                <th className="px-6 py-4">Entreprise</th>
                                <th className="px-6 py-4">Pack Demandé</th>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4">Statut</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {requests.length > 0 ? (
                                requests.map((req) => (
                                    <tr key={req._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">
                                                    <User className="w-4 h-4" />
                                                </div>
                                                <span className="font-medium text-slate-900 dark:text-white">{req.admin?.email}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                                                <Building className="w-4 h-4" />
                                                {req.recruiter?.company_name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <Package className="w-4 h-4 text-blue-500" />
                                                <span className="font-medium text-slate-900 dark:text-white">{req.pack?.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-500">
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-4 h-4" />
                                                <FormattedDate date={req.createdAt} />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                                req.status === 'approved' ? 'bg-green-100 text-green-700' :
                                                    'bg-red-100 text-red-700'
                                                }`}>
                                                {req.status === 'pending' ? 'En attente' :
                                                    req.status === 'approved' ? 'Approuvé' : 'Rejeté'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {req.status === 'pending' && (
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleApprove(req._id)}
                                                        className="p-2 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                                        title="Approuver"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleReject(req._id)}
                                                        className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                        title="Rejeter"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                                        Aucune demande en attente
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PackRequests;
